import {
  checkCompatibility,
  type CompatibilityResult,
} from '@streamlain/field-types';

import { autoMatchFields } from '../../sidebar/utils/auto-match-fields';
import type { CapabilityRequirement } from '../../types/capability-schema';
import {
  type BindingResolverInput,
  type BindingResolverOutput,
  type CapabilityBindingResult,
  type DbColumnInfo,
  dbColumnToResponseField,
  requirementToTargetColumn,
} from './types';

const COMPAT_ORDER: Record<CompatibilityResult, number> = {
  compatible: 0,
  castable: 1,
  incompatible: 2,
};

function bestCompatibility(
  acceptedTypes: string[],
  sourceType: string
): CompatibilityResult {
  let best: CompatibilityResult = 'incompatible';
  for (const t of acceptedTypes) {
    const r = checkCompatibility(t, sourceType);
    if (COMPAT_ORDER[r] < COMPAT_ORDER[best]) best = r;
    if (best === 'compatible') return best;
  }
  return best;
}

function bindingKey(role: string, field: string): string {
  return `${role}:${field}`;
}

export function resolveBindings(
  input: BindingResolverInput
): BindingResolverOutput {
  const { capabilities, columns, overrides = {} } = input;

  const columnById = new Map<string, DbColumnInfo>(columns.map(c => [c.id, c]));
  const responseFields = columns.map(dbColumnToResponseField);

  type FlatReq = {
    key: string;
    role: string;
    req: CapabilityRequirement;
  };
  const flatReqs: FlatReq[] = [];
  for (const cap of capabilities) {
    for (const req of cap.requirements) {
      flatReqs.push({
        key: bindingKey(cap.role, req.field),
        role: cap.role,
        req,
      });
    }
  }

  const overrideKeys = new Set(Object.keys(overrides));

  const autoTargets = flatReqs
    .filter(f => !overrideKeys.has(f.key))
    .flatMap(f => {
      const cap = capabilities.find(c => c.role === f.role);
      if (!cap) return [];
      return [requirementToTargetColumn(cap, f.req)];
    });

  const suggestions = autoMatchFields(responseFields, autoTargets);
  const suggestionByKey = new Map(
    suggestions.map(s => [s.targetColumnId, s] as const)
  );

  const bindings: Record<string, CapabilityBindingResult> = {};
  const missingRequired: string[] = [];

  for (const { key, req } of flatReqs) {
    const override = overrides[key];
    if (override !== undefined) {
      const col = columnById.get(override);
      const compatibility = col
        ? bestCompatibility(req.acceptedTypes, col.type)
        : 'incompatible';
      bindings[key] = {
        role: key,
        columnId: col ? override : null,
        compatibility,
        source: 'override',
        alternatives: [],
      };
      if (req.required && !col) missingRequired.push(key);
      continue;
    }

    const suggestion = suggestionByKey.get(key);
    if (!suggestion) {
      bindings[key] = {
        role: key,
        columnId: null,
        compatibility: 'incompatible',
        source: 'unresolved',
        alternatives: [],
      };
      if (req.required) missingRequired.push(key);
      continue;
    }

    const columnId = suggestion.responseField;
    const col = columnById.get(columnId);
    const compatibility = col
      ? bestCompatibility(req.acceptedTypes, col.type)
      : 'incompatible';

    const alternatives = suggestion.alternatives
      .filter(a => a.responseField !== columnId)
      .map(a => {
        const altCol = columnById.get(a.responseField);
        return {
          columnId: a.responseField,
          columnName: altCol?.name ?? a.responseFieldName ?? a.responseField,
          compatibility: altCol
            ? bestCompatibility(req.acceptedTypes, altCol.type)
            : ('incompatible' as CompatibilityResult),
        };
      })
      .sort(
        (a, b) => COMPAT_ORDER[a.compatibility] - COMPAT_ORDER[b.compatibility]
      );

    bindings[key] = {
      role: key,
      columnId,
      compatibility,
      source: 'auto',
      alternatives,
    };
  }

  return { bindings, missingRequired };
}
